import { createAsyncThunk } from "@reduxjs/toolkit";

import { getOrganizationId, isJson } from "Libs/utils";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";

import type {
  GetOrganizationProps,
  GetOrganizationPayloadFulfilled
} from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const getOrganization = createAsyncThunk<
  GetOrganizationPayloadFulfilled,
  GetOrganizationProps,
  AsyncThunkOptionType
>(
  "app/organizations/get_organization",
  async (
    { organizationDescriptionId, shouldUpdateState = false },
    { rejectWithValue, getState, dispatch }
  ) => {
    const organization = organizationByDescriptionIdSelector(getState(), {
      organizationDescriptionId
    });
    const shouldReloadOrganization =
      !organization || !(organization && "_links" in organization);

    if (shouldReloadOrganization) {
      // Important to note that LOAD_REF_ORGANIZATIONS does not return full organization response data
      // like _links for some permission operations on organization. So if there is no organization or
      // such organization does not have _links it is likely that it is a response from ref organization
      // lets load the full organization to replace the ref organization

      const platformLib = await import("Libs/platform");
      const client = platformLib.default;

      let organizationId = getOrganizationId(
        getState,
        organizationDescriptionId
      );

      // For admins, they may not have the organization in their /organizations response
      if (!organizationId) {
        organizationId = `name=${organizationDescriptionId}`;
      }

      try {
        const organization = await client.getOrganization(organizationId);
        // We have some especial organizations where the organization name is the
        // same as the organization id but in lowercase. The request above
        // attempts to find an organization by name but the endpoint is not case
        // sensitive. For those organizations console will have a hard time with
        // this and will fail to find the stored organization.
        // The catch side of this block will handle requesting the organization by
        // id and doing a clean redirect to the url with the organization name
        if (
          organization.id === organizationDescriptionId &&
          organization.name === organizationDescriptionId.toLowerCase()
        ) {
          const error = new Error("unnamed organization");
          return rejectWithValue({
            error,
            redirectTo: `/${organization.name}`
          });
        }

        return { organization, shouldUpdateState };
      } catch (err) {
        if (isJson(err)) {
          const errorAsJSON = JSON.parse(err);
          // The user needs either MFA or SSO to access the organization
          if (errorAsJSON.error === "insufficient_user_authentication") {
            // We need to patch the error since we don't have enough information
            // from the client.js
            errorAsJSON.status = 401;
            if (shouldUpdateState) {
              return rejectWithValue({ error: errorAsJSON, shouldUpdateState });
            }
            return;
          }
        }

        // Lets try loading the organization as it is without assuming that it is the organization name
        if (shouldUpdateState) {
          dispatch(
            getOrganization({
              organizationDescriptionId:
                organizationDescriptionId?.toUpperCase(),
              shouldUpdateState
            })
          );
        }

        // We don't use organization actual Id in console
        // lets redirect to organization name id after we
        // have successfully fetched the organization using it actual ID
        // Effectively makes withProjectRedirect HOC wrapper obsolete
        if (organization?.name) {
          const newPath = location.pathname.replace(
            organizationDescriptionId,
            organization.name
          );

          return {
            organization,
            shouldUpdateState: false,
            redirectTo: newPath
          };
        }
      }
    }

    return { organization, shouldUpdateState: false };
  }
);
